import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';

// @@REPORT_REVIEW_v115@@ Admin report review for ONE Shopify store: reports paged by year, every line expands in place
// (full summary, lazy-loaded once and cached), credit-note + client-notified status on the line, one-click actions.
// Presentational + its own view state; all mutations (PDF / credit note / notify / delete) are callbacks into ClientDetail
// so messages, busy flags and reloads stay in one place.
const DASH = '—';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function ymd(v) { return v ? String(v).slice(0, 10) : ''; }
function periodLabel(type, start) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd(start));
  if (!m) return ymd(start) || DASH;
  const mo = parseInt(m[2], 10);
  if (type === 'quarter') return 'Q' + (Math.floor((mo - 1) / 3) + 1) + ' ' + m[1];
  if (type === 'month') return MONTHS[mo - 1] + ' ' + m[1];
  return MONTHS[mo - 1] + ' ' + parseInt(m[3], 10) + ', ' + m[1];
}
function money(v) {
  // @@COGS_PAYOUT_v116@@ negative payouts (COGS larger than the artist share) read -$1,234.00, not $-1,234.00
  if (v == null) return DASH;
  const abs = Math.abs(Number(v)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (Number(v) < 0 ? '-$' : '$') + abs;
}
function pct(v) { return v == null ? DASH : (Number(v) * 100).toFixed(2) + '%'; }
function shortDate(d) {
  if (!d) return DASH;
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? DASH : x.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// @@CN_LINK_SHORT_v115_6@@ "CN-2026-10-AZ (October 2026 Store Royalties)" -> "CN-2026-10-AZ" (drop the trailing "(…)", cap the rest).
function shortCn(num) {
  const s = String(num || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
  return s.length > 22 ? s.slice(0, 21) + '…' : s;
}

function CreditNoteCell({ r }) {
  const cn = r.credit_note;
  if (!cn) return <span className="hq-cd-muted">{DASH}</span>;
  const changed = cn.amount != null && r.artist_due != null && Math.abs(Number(r.artist_due) - Number(cn.amount)) > 0.005;
  return (
    <span>
      {cn.url
        ? <a className="hq-cd-a" style={{ whiteSpace: 'nowrap' }} href={cn.url} target="_blank" rel="noreferrer" title={(cn.number || 'Credit note') + ' — open in Zoho Books'}>{shortCn(cn.number) || 'Credit note'} ↗</a>
        : <b title={cn.number || ''}>{shortCn(cn.number) || 'Linked'}</b>}
      {cn.created_at && <span className="hq-cd-muted"> · {shortDate(cn.created_at)}</span>}
      {changed && <span className="hq-rr-warn" title={'Credit note is for ' + money(cn.amount) + ' but this report now says ' + money(r.artist_due) + ' — review in Zoho'}>⚠ amount changed</span>}
    </span>
  );
}

function NotifiedCell({ r }) {
  if (!r.client_notified_at) return <span className="hq-cd-muted">Not yet</span>;
  return <span className="hq-rr-ok">✓ {shortDate(r.client_notified_at)}{r.client_notified_count > 1 ? ' ×' + r.client_notified_count : ''}</span>;
}

function ReportBody({ summary }) {
  const s = summary || {};
  const h = s.headline || {};
  const t = s.traffic || {};
  const intl = s.international || null;
  const notUs = (c) => { const cc = String(c.country == null ? '' : c.country).trim().toLowerCase(); return cc !== 'united states' && cc !== 'us' && cc !== 'usa'; };
  return (
    <>
      <div className="hq-cd-minitiles">
        <div><span>Orders</span><b>{h.orders == null ? DASH : h.orders}</b></div>
        <div><span>Net sales</span><b>{money(h.net_sales)}</b></div>
        <div><span>Items sold</span><b>{h.net_items_sold == null ? DASH : h.net_items_sold}</b></div>
        <div><span>Visitors</span><b>{t.sessions == null ? DASH : t.sessions}</b></div>
        <div><span>Conversion</span><b>{pct(t.conversion_rate)}</b></div>
      </div>
      {s.payout && (
        <dl className="hq-cd-dl hq-cd-payout">
          <dt>Net sales</dt><dd>{money(s.payout.net_sales)}</dd>
          <dt>XYZ commission</dt><dd>{s.payout.commission_pct}% ({money(s.payout.xyz_cut)})</dd>
          {s.payout.cogs != null && (<>{/* @@COGS_PAYOUT_v116@@ */}
            <dt>Less: cost of goods</dt>
            <dd>{money(s.payout.cogs)}{Number(s.payout.cogs_lines_missing) > 0 && <span className="hq-rr-warn" title="Lines whose product has no cost in Shopify are left out of COGS (not counted as $0)."> ⚠ {s.payout.cogs_lines_missing} line(s) had no cost</span>}</dd>
          </>)}
          <dt>Amount due to artist</dt><dd><strong>{money(s.payout.artist_due)}</strong></dd>
        </dl>
      )}
      <div className="hq-cd-reportgrid">
        <div>
          <h4>Top 10 items</h4>
          <table className="hq-table"><tbody>
            {(s.top_items || []).slice(0, 10).map((it, i) => (
              <tr key={i}><td>{it.product_title}</td><td style={{ textAlign: 'right' }}>{it.net_items_sold}</td></tr>
            ))}
          </tbody></table>
        </div>
        <div>
          <h4>Top 5 states</h4>
          <table className="hq-table"><tbody>
            {(s.top_states || []).map((x, i) => (
              <tr key={i}><td>{x.region}</td><td style={{ textAlign: 'right' }}>{money(x.net_sales)}</td></tr>
            ))}
          </tbody></table>
        </div>
        <div>
          <h4>Sales tax by state</h4>
          <table className="hq-table"><tbody>
            {(s.tax_by_region || []).map((x, i) => (
              <tr key={i}><td>{x.region}</td><td style={{ textAlign: 'right' }}>{money(x.taxes)}</td></tr>
            ))}
          </tbody></table>
        </div>
        {intl && (
          <div>
            <h4>International — {money(intl.intl_total_net_sales)} · {intl.intl_total_orders} orders</h4>
            <table className="hq-table"><tbody>
              {(intl.by_country || []).filter(notUs).map((c, i) => (
                <tr key={i}><td>{c.country}</td><td style={{ textAlign: 'right' }}>{money(c.net_sales)}</td><td style={{ textAlign: 'right' }}>{c.orders}</td></tr>
              ))}
            </tbody></table>
          </div>
        )}
      </div>
    </>
  );
}

export default function ReportsReview({
  clientId, storeId, reports, isManager, focus, cnBusy, notifyBusy,
  onPdf, onCreditNote, onNotify, onDelete, onLinkExisting, linkBusy, // @@CN_LINK_EXISTING_v115_3@@
}) {
  const [year, setYear] = useState('');
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState({});       // reportId -> true
  const [detail, setDetail] = useState({});   // reportId -> full report | { error: true }
  const fetching = useRef({});

  const years = useMemo(
    () => Array.from(new Set(reports.map((r) => ymd(r.period_start).slice(0, 4)).filter(Boolean))).sort().reverse(),
    [reports]
  );
  // keep the selected year valid (first load, after a delete)
  useEffect(() => { if (years.length && !years.includes(year)) setYear(years[0]); }, [years, year]);

  const ensureDetail = useCallback(async (reportId) => {
    if (fetching.current[reportId]) return;
    fetching.current[reportId] = true;
    try {
      const res = await apiFetch('/api/clients/' + clientId + '/shopify-stores/' + storeId + '/reports/' + reportId);
      if (res.ok) { const d = await res.json(); setDetail((p) => ({ ...p, [reportId]: d.report })); }
      else setDetail((p) => ({ ...p, [reportId]: { error: true } }));
    } catch (e) {
      setDetail((p) => ({ ...p, [reportId]: { error: true } }));
    } finally { fetching.current[reportId] = false; }
  }, [clientId, storeId]);

  // Reports reloaded (regenerate / delete): drop cached bodies that are stale or gone, and forget open flags for gone rows.
  useEffect(() => {
    const byId = {}; reports.forEach((r) => { byId[r.id] = r; });
    setDetail((d) => {
      const keep = {}; let changed = false;
      Object.keys(d).forEach((k) => {
        if (byId[k] && d[k] && d[k].generated_at === byId[k].generated_at) keep[k] = d[k]; else changed = true;
      });
      return changed ? keep : d;
    });
    setOpen((o) => {
      const keep = {}; let changed = false;
      Object.keys(o).forEach((k) => { if (byId[k]) keep[k] = o[k]; else changed = true; });
      return changed ? keep : o;
    });
  }, [reports]);

  // Any open row without a cached body gets fetched (covers first expand, expand-all and the stale-drop above).
  useEffect(() => {
    Object.keys(open).forEach((k) => { if (open[k] && !detail[k]) ensureDetail(k); });
  }, [open, detail, ensureDetail]);

  // A report was just generated: jump to its year and open it.
  useEffect(() => {
    if (!focus || !focus.id) return;
    const r = reports.find((x) => x.id === focus.id);
    if (r) setYear(ymd(r.period_start).slice(0, 4));
    setFilter('all');
    setOpen((o) => ({ ...o, [focus.id]: true }));
  }, [focus]); // deliberately NOT [reports]: only a new "focus" (just-generated report) may pull the view

  const inYear = useMemo(() => reports.filter((r) => ymd(r.period_start).slice(0, 4) === year), [reports, year]);
  const needCn = inYear.filter((r) => !r.credit_note).length;
  const needNotify = inYear.filter((r) => !r.client_notified_at).length;
  const rows = useMemo(() => inYear
    .filter((r) => (filter === 'no_cn' ? !r.credit_note : filter === 'not_notified' ? !r.client_notified_at : true))
    .sort((a, b) => ymd(b.period_start).localeCompare(ymd(a.period_start)) || String(a.period_type).localeCompare(String(b.period_type))),
  [inYear, filter]);

  if (!reports.length) {
    return <p className="hq-cd-muted">No reports yet. Pick a period (first day of month/quarter) and Generate.</p>;
  }

  const yi = years.indexOf(year);
  const older = yi >= 0 ? years[yi + 1] : null;
  const newer = yi > 0 ? years[yi - 1] : null;
  const toggle = (rid) => setOpen((o) => ({ ...o, [rid]: !o[rid] }));
  const expandAll = () => setOpen((o) => { const n = { ...o }; rows.forEach((r) => { n[r.id] = true; }); return n; });
  const collapseAll = () => setOpen((o) => { const n = { ...o }; rows.forEach((r) => { delete n[r.id]; }); return n; });
  const cols = isManager ? 9 : 8;

  return (
    <div className="hq-rr">
      <div className="hq-rr-bar">
        <div className="hq-rr-pager">
          <button type="button" className="hq-btn hq-btn-ghost hq-cd-sm" disabled={!older} onClick={() => setYear(older)} title={older ? 'Go to ' + older : ''}>‹</button>
          <span className="hq-rr-year">{year}</span>
          <button type="button" className="hq-btn hq-btn-ghost hq-cd-sm" disabled={!newer} onClick={() => setYear(newer)} title={newer ? 'Go to ' + newer : ''}>›</button>
        </div>
        <div className="hq-rr-chips">
          {years.map((y) => (
            <button type="button" key={y} className={'hq-rr-chip' + (y === year ? ' hq-rr-chip-on' : '')} onClick={() => setYear(y)}>
              {y}<i>{reports.filter((r) => ymd(r.period_start).slice(0, 4) === y).length}</i>
            </button>
          ))}
        </div>
        <span className="hq-rr-sp" />
        <select className="hq-select" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter reports">
          <option value="all">All reports ({inYear.length})</option>
          <option value="no_cn">No credit note ({needCn})</option>
          <option value="not_notified">Client not notified ({needNotify})</option>
        </select>
        <button type="button" className="hq-btn hq-btn-ghost hq-cd-sm" onClick={expandAll}>Expand all</button>
        <button type="button" className="hq-btn hq-btn-ghost hq-cd-sm" onClick={collapseAll}>Collapse all</button>
        {isManager && onLinkExisting && reports.some((r) => !r.credit_note) && (
          <button type="button" className="hq-btn hq-btn-ghost hq-cd-sm" disabled={!!linkBusy} onClick={onLinkExisting}
            title="Look in Zoho for credit notes already created for these reports and link them. Never creates anything.">{linkBusy ? 'Looking in Zoho…' : 'Link existing credit notes'}</button>
        )}
      </div>

      <div className="hq-rr-scroll">
        <table className="hq-table hq-rr-table">
          <thead>
            <tr>
              <th style={{ width: 28 }}></th><th>Period</th><th>Type</th>
              <th style={{ textAlign: 'right' }}>Net sales</th>
              {isManager && <th style={{ textAlign: 'right' }}>Artist due</th>}
              <th>Credit note</th><th>Client notified</th><th>Generated</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={cols} className="hq-cd-muted">Nothing matches this filter for {year}.</td></tr>
            )}
            {rows.map((r) => {
              const isOpen = !!open[r.id];
              const d = detail[r.id];
              return (
                <React.Fragment key={r.id}>
                  <tr className={isOpen ? 'hq-cd-rowon' : ''}>
                    <td>
                      <button type="button" className="hq-rr-toggle" aria-expanded={isOpen}
                        aria-label={(isOpen ? 'Collapse ' : 'Expand ') + periodLabel(r.period_type, r.period_start)}
                        onClick={() => toggle(r.id)}>{isOpen ? '▾' : '▸'}</button>
                    </td>
                    <td>
                      <button type="button" className="hq-rr-link" onClick={() => toggle(r.id)}><b>{periodLabel(r.period_type, r.period_start)}</b></button>
                      {r.reference && <div className="hq-cd-muted hq-rr-ref">{r.reference}</div>}
                    </td>
                    <td style={{ textTransform: 'capitalize' }}>{r.period_type}</td>
                    <td style={{ textAlign: 'right' }}>{money(r.net_sales)}</td>
                    {isManager && <td style={{ textAlign: 'right' }}>{money(r.artist_due)}</td>}
                    <td><CreditNoteCell r={r} /></td>
                    <td><NotifiedCell r={r} /></td>
                    <td className="hq-cd-muted">{shortDate(r.generated_at)}</td>
                    <td className="hq-cd-actcell">
                      <button type="button" className="hq-btn hq-btn-ghost hq-cd-sm" onClick={() => onPdf(storeId, r.id)}>PDF</button>
                      {isManager && !r.credit_note && (
                        <button type="button" className="hq-btn hq-btn-ghost hq-cd-sm" disabled={!!cnBusy[r.id]}
                          onClick={() => onCreditNote(storeId, r.id)}>{cnBusy[r.id] ? 'Creating…' : 'Credit note'}</button>
                      )}
                      {isManager && (
                        <button type="button" className="hq-btn hq-btn-ghost hq-cd-sm" disabled={!!notifyBusy[r.id]}
                          title="Email the client's portal users that this report is ready"
                          onClick={() => onNotify(storeId, r)}>{notifyBusy[r.id] ? 'Sending…' : r.client_notified_at ? 'Resend' : 'Notify'}</button>
                      )}
                      {isManager && (
                        <button type="button" className="hq-btn hq-btn-ghost hq-cd-sm hq-cd-danger" onClick={() => onDelete(storeId, r.id)}>Delete</button>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="hq-rr-exp">
                      <td colSpan={cols}>
                        <div className="hq-rr-status">
                          <div><span className="k">Credit note</span><CreditNoteCell r={r} />
                            {isManager && r.credit_note && (
                              <button type="button" className="hq-btn hq-btn-ghost hq-cd-sm" disabled={!!cnBusy[r.id]} onClick={() => onCreditNote(storeId, r.id)}>Create another…</button>
                            )}
                          </div>
                          <div><span className="k">Client notified</span><NotifiedCell r={r} /></div>
                          <div><span className="k">Generated</span><span>{shortDate(r.generated_at)}</span></div>
                        </div>
                        {!d && <p className="hq-cd-muted">Loading…</p>}
                        {d && d.error && <p className="hq-cd-muted">Could not load this report. Collapse and expand to retry.</p>}
                        {d && !d.error && <ReportBody summary={d.summary} />}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
